import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, LogIn } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(form.email, form.password);
      toast.success("Welcome back");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left panel */}
      <div className="hidden lg:flex bg-[#06152D] text-white p-12 flex-col justify-between lj-grain relative">
        <div><Logo size={44} variant="light" withWordmark /></div>
        <div>
          <h1 className="font-display text-4xl font-bold tracking-tight leading-tight">Loan Junction CRM</h1>
          <p className="mt-4 text-[#B5C4E1] max-w-md">Sign in to manage leads, customers, loan files, insurance renewals, and daily operations for the Siddipet branch.</p>
          <div className="mt-8 grid grid-cols-2 gap-4 max-w-md text-sm">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="font-mono-num text-2xl font-bold">100%</div>
              <div className="text-xs text-[#B5C4E1] mt-1">Role-scoped access</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="font-mono-num text-2xl font-bold">GPS</div>
              <div className="text-xs text-[#B5C4E1] mt-1">Attendance</div>
            </div>
          </div>
        </div>
        <div className="text-xs text-[#B5C4E1]">© {new Date().getFullYear()} Loan Junction · Siddipet</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden mb-8"><Logo size={44} withWordmark /></div>
          <h2 className="font-display text-3xl font-bold text-[#06152D]">Sign in</h2>
          <p className="mt-2 text-sm text-[#52525B]">Employee access · Loan Junction CRM</p>

          <div className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })}
                     data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={form.password}
                     onChange={(e) => setForm({ ...form, password: e.target.value })}
                     data-testid="login-password" />
            </div>
            <button type="submit" disabled={busy}
                    className="w-full bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="login-submit">
              {busy ? <span>Signing in…</span> : <><LogIn size={16} /> Sign in</>}
            </button>
          </div>

          <div className="mt-6 flex items-center gap-2 text-xs text-[#52525B]">
            <Lock size={12} /> Secured with JWT · Role-based access · Audit-logged
          </div>
          <div className="mt-8 pt-6 border-t border-[#E4E4E7] text-xs text-[#52525B]">
            <Link to="/" className="hover:text-[#0052FF]" data-testid="login-back-home">← Back to website</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
